/**
 * useReportMeta — Sets document title + OpenGraph / Twitter meta tags
 * for the report. Used to give shared links a proper preview card
 * (title, description, image) on platforms that scrape link previews.
 *
 * CRITICAL LIMITATION: this runs in the browser, after the JS bundle
 * loads. Crawlers that DO execute JS (Twitter/X, Discord, modern
 * Slack) will pick up these tags. Crawlers that DON'T (Facebook,
 * LinkedIn, many Slack instances depending on version, Telegram in
 * places) will only see the static index.html — so they'll show the
 * site-wide Kuutio meta, not the report-specific one.
 *
 * To make this work for every crawler we'd need to inject meta tags
 * server-side — either a Vercel serverless function as a rewrite
 * target for /raportti/:slug, or a backend route that returns the
 * SPA shell with meta tags pre-rendered. That's a separate change
 * we haven't shipped; this hook is the best-effort first step.
 *
 * Tags managed:
 *   <title>
 *   <meta name="description">
 *   <meta property="og:title">
 *   <meta property="og:description">
 *   <meta property="og:image">
 *   <meta property="og:url">
 *   <meta property="og:type">
 *   <meta property="og:site_name">
 *   <meta name="twitter:card">
 *   <meta name="twitter:title">
 *   <meta name="twitter:description">
 *   <meta name="twitter:image">
 *
 * On unmount, the previous values are restored so other routes don't
 * inherit report-specific meta.
 */
import { useEffect } from 'react';

const TAG_KEY = 'data-kuutio-report-meta';

/**
 * Apply an upsert: find a tag matching the selector under <head>,
 * setting (or creating) the requested attribute. Marks managed tags
 * with data-kuutio-report-meta so we can wipe them on cleanup.
 */
function setMeta({ selector, createTag, attr, value }) {
  if (!value) return null;
  const head = document.head;
  let el = head.querySelector(selector);
  let created = false;
  if (!el) {
    el = createTag();
    el.setAttribute(TAG_KEY, '1');
    head.appendChild(el);
    created = true;
  }
  const previous = el.getAttribute(attr);
  el.setAttribute(attr, value);
  return { el, attr, previous, created };
}

export function useReportMeta({ title, description, imageUrl, url, siteName = 'Kuutio · Sitra' }) {
  useEffect(() => {
    if (typeof document === 'undefined') return;

    const previousDocTitle = document.title;
    if (title) document.title = title;

    const ops = [
      // Description (plain)
      setMeta({
        selector: 'meta[name="description"]',
        createTag: () => { const m = document.createElement('meta'); m.setAttribute('name', 'description'); return m; },
        attr: 'content',
        value: description,
      }),
      // OpenGraph
      setMeta({
        selector: 'meta[property="og:title"]',
        createTag: () => { const m = document.createElement('meta'); m.setAttribute('property', 'og:title'); return m; },
        attr: 'content',
        value: title,
      }),
      setMeta({
        selector: 'meta[property="og:description"]',
        createTag: () => { const m = document.createElement('meta'); m.setAttribute('property', 'og:description'); return m; },
        attr: 'content',
        value: description,
      }),
      setMeta({
        selector: 'meta[property="og:image"]',
        createTag: () => { const m = document.createElement('meta'); m.setAttribute('property', 'og:image'); return m; },
        attr: 'content',
        value: imageUrl,
      }),
      setMeta({
        selector: 'meta[property="og:url"]',
        createTag: () => { const m = document.createElement('meta'); m.setAttribute('property', 'og:url'); return m; },
        attr: 'content',
        value: url,
      }),
      setMeta({
        selector: 'meta[property="og:type"]',
        createTag: () => { const m = document.createElement('meta'); m.setAttribute('property', 'og:type'); return m; },
        attr: 'content',
        value: 'article',
      }),
      setMeta({
        selector: 'meta[property="og:site_name"]',
        createTag: () => { const m = document.createElement('meta'); m.setAttribute('property', 'og:site_name'); return m; },
        attr: 'content',
        value: siteName,
      }),
      // Twitter
      setMeta({
        selector: 'meta[name="twitter:card"]',
        createTag: () => { const m = document.createElement('meta'); m.setAttribute('name', 'twitter:card'); return m; },
        attr: 'content',
        value: imageUrl ? 'summary_large_image' : 'summary',
      }),
      setMeta({
        selector: 'meta[name="twitter:title"]',
        createTag: () => { const m = document.createElement('meta'); m.setAttribute('name', 'twitter:title'); return m; },
        attr: 'content',
        value: title,
      }),
      setMeta({
        selector: 'meta[name="twitter:description"]',
        createTag: () => { const m = document.createElement('meta'); m.setAttribute('name', 'twitter:description'); return m; },
        attr: 'content',
        value: description,
      }),
      setMeta({
        selector: 'meta[name="twitter:image"]',
        createTag: () => { const m = document.createElement('meta'); m.setAttribute('name', 'twitter:image'); return m; },
        attr: 'content',
        value: imageUrl,
      }),
    ].filter(Boolean);

    return () => {
      document.title = previousDocTitle;
      for (const op of ops) {
        if (op.created) {
          op.el.parentNode?.removeChild(op.el);
        } else if (op.previous !== null) {
          op.el.setAttribute(op.attr, op.previous);
        } else {
          op.el.removeAttribute(op.attr);
        }
      }
    };
  }, [title, description, imageUrl, url, siteName]);
}
